import {
  Component,
  ViewChild,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DateTime } from 'luxon';
import { firstValueFrom } from 'rxjs';

import {
  AuthStore,
  CancelButtonComponent,
  ErrorAlertComponent,
  FeatureHeaderComponent,
  PERMISSIONS,
} from '@sandbox-platform/shared-ui';

import {
  CommissionInvoiceDetailStore,
  CommissionInvoiceDetailStoreType,
} from './+state/commission-invoice-detail.store';
import { INVOICE_STATUS_LABELS } from '../../../types/commission-invoice.types';
import {
  CreateCommissionPaymentDTO,
  ReverseCommissionPaymentDTO,
} from '../../../types/commission-payment.types';
import { ExpertApiService } from '../../../services/expert-api.service';
import { ExpertDTO } from '../../../types/expert.types';
import { CommissionInvoiceStatusBadgeComponent } from '../list/components/commission-invoice-status-badge.component';
import { AddPaymentModalComponent } from './components/add-payment-modal.component';
import { CommissionInvoiceLineItemsComponent } from './components/commission-invoice-line-items.component';
import { CommissionInvoicePaymentHistoryComponent } from './components/commission-invoice-payment-history.component';
import { ReversePaymentModalComponent } from './components/reverse-payment-modal.component';
import { canUserManageCommissionPayment } from './commission-payment-permission.util';

@Component({
  standalone: true,
  imports: [
    CancelButtonComponent,
    ErrorAlertComponent,
    FeatureHeaderComponent,
    CommissionInvoiceStatusBadgeComponent,
    CommissionInvoiceLineItemsComponent,
    CommissionInvoicePaymentHistoryComponent,
    AddPaymentModalComponent,
    ReversePaymentModalComponent,
  ],
  selector: 'lib-commission-invoice-detail',
  templateUrl: './commission-invoice-detail.component.html',
  host: { class: 'block h-full overflow-y-auto' },
})
export class CommissionInvoiceDetailComponent implements OnInit {
  @ViewChild(AddPaymentModalComponent)
  addPaymentModal?: AddPaymentModalComponent;

  @ViewChild(ReversePaymentModalComponent)
  reversePaymentModal?: ReversePaymentModalComponent;

  protected readonly store: CommissionInvoiceDetailStoreType = inject(
    CommissionInvoiceDetailStore
  );
  protected readonly authStore = inject(AuthStore);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly expertApi = inject(ExpertApiService);

  protected readonly invoiceId = this.route.snapshot.paramMap.get('id');
  protected readonly currentExpert = signal<ExpertDTO | null>(null);

  protected readonly canAddPayment = computed(
    () => this.store.canAddPayment() && this.canManagePayment()
  );
  protected readonly canApprove = computed(
    () =>
      this.store.canApprove() &&
      this.authStore.hasExpertPermission(
        PERMISSIONS.EXPERT.COMMISSION_INVOICE_APPROVE
      )
  );
  protected readonly canCancel = computed(
    () =>
      this.store.canCancel() &&
      this.authStore.hasExpertPermission(
        PERMISSIONS.EXPERT.COMMISSION_INVOICE_CANCEL
      )
  );
  protected readonly canViewPayments = computed(() =>
    this.authStore.hasExpertPermission(
      PERMISSIONS.EXPERT.COMMISSION_PAYMENT_READ
    )
  );
  protected readonly canReversePayment = computed(() => this.canManagePayment());

  private readonly canManagePayment = computed(() => {
    const invoice = this.store.invoice();
    if (!invoice) return false;

    return canUserManageCommissionPayment(
      invoice,
      this.currentUserWithExpertOrganization(),
      (permission) => this.authStore.hasExpertPermission(permission),
      {
        createAny: PERMISSIONS.EXPERT.COMMISSION_PAYMENT_CREATE_ANY,
      }
    );
  });

  async ngOnInit(): Promise<void> {
    this.store.reset();
    if (this.invoiceId) {
      await this.store.loadInvoice(this.invoiceId);
      if (this.canViewPayments()) {
        await this.store.loadPayments(this.invoiceId);
      }
    }
    await this.loadCurrentExpert();
  }

  protected goBack(): void {
    this.router.navigate(['/commission-invoices']);
  }

  protected onOpenAddPayment(): void {
    setTimeout(() => this.addPaymentModal?.open());
  }

  protected async onAddPayment(dto: CreateCommissionPaymentDTO): Promise<void> {
    await this.store.addPayment(dto);
  }

  protected onOpenReversePayment(paymentId: string): void {
    setTimeout(() => this.reversePaymentModal?.open(paymentId));
  }

  protected async onReversePayment(event: {
    paymentId: string;
    dto: ReverseCommissionPaymentDTO;
  }): Promise<void> {
    await this.store.reversePayment(event.paymentId, event.dto);
  }

  protected async onApprove(): Promise<void> {
    if (this.invoiceId) {
      await this.store.approve(this.invoiceId);
    }
  }

  protected async onCancel(): Promise<void> {
    if (this.invoiceId) {
      await this.store.cancel(this.invoiceId);
    }
  }

  protected statusLabel(status: string): string {
    return (
      INVOICE_STATUS_LABELS[status as keyof typeof INVOICE_STATUS_LABELS] ??
      status
    );
  }

  protected titleCase(value?: string): string {
    if (!value) return '-';
    return value
      .replace(/_/g, ' ')
      .replace(/\w\S*/g, (t) => t.charAt(0).toUpperCase() + t.slice(1));
  }

  protected formatDate(date?: string | Date): string {
    if (!date) return '-';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (Number.isNaN(dateObj.getTime()) || dateObj.getFullYear() <= 1)
      return '-';
    return DateTime.fromJSDate(dateObj).toFormat('dd-MMM-yyyy');
  }

  protected formatMoney(amountMinor?: number, currency = 'KES'): string {
    if (amountMinor === undefined || amountMinor === null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amountMinor / 100);
  }

  private async loadCurrentExpert(): Promise<void> {
    const expertId = this.authStore.currentUserExpertId();
    if (!expertId) return;

    try {
      const response = await firstValueFrom(this.expertApi.getById(expertId));
      this.currentExpert.set(response.data);
    } catch {
      this.currentExpert.set(null);
    }
  }

  private currentUserWithExpertOrganization() {
    const user = this.authStore.user();
    const organizationId = this.currentExpert()?.organization_id;
    if (!user || !organizationId) return user;

    return {
      ...user,
      organization_id: organizationId,
    };
  }
}
